import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { GoogleQRResponse, RecoveryCodesResponse, UserSettingsService } from '../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { OtpCodeInputComponent } from '../otp-code-input/otp-code-input.component';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';

/**
 * Authenticator-app enrollment as a self-contained, dialog-agnostic step: fetch + show
 * the QR (and manual secret), take the 6-digit code via the shared OTP control, and call
 * `finishGoogleAuthSetup`. Emits `completed` once enrolled and `cancelled` on back-out, so a
 * host (the 2FA gate stepper, or a Settings dialog wrapper) drives navigation. No MatDialogRef
 * dependency (SD-8102).
 */
@UntilDestroy()
@Component({
    selector: 'app-google-auth-setup-step',
    templateUrl: './google-auth-setup-step.component.html',
    styleUrls: ['./two-factor-setup-step.shared.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, MatProgressSpinnerModule, OtpCodeInputComponent, FlexSpacerDirective, RecoveryCodesPanelComponent],
})
export class GoogleAuthSetupStepComponent implements OnInit, OnDestroy
{
    /**
     * Label for the dismiss button. Defaults to 'Cancel' (the step is opened directly, so backing
     * out closes it). The gate dialog opens the step from a method chooser and passes 'Back' — the
     * dismiss returns to that chooser rather than closing the flow.
     */
    @Input() dismissLabel = 'Cancel';

    @Output() completed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    /**
     * Fires when the step reaches its show-once recovery-codes stage, so the host can swap its
     * header — the codes render inside the host's surface, not a dialog of their own.
     */
    @Output() showingRecoveryCodes = new EventEmitter<void>();

    @ViewChild(OtpCodeInputComponent) private _otp: OtpCodeInputComponent;

    qrCode: SafeHtml | null = null;
    secretKey = '';
    code = '';
    isSaving = false;
    isSuccess = false;
    hasError = false;

    /** 'setup' takes the QR + 6-digit code; 'codes' is the show-once recovery-codes stage. */
    stage: 'setup' | 'codes' = 'setup';
    recoveryCodes: string[] = [];
    codesAcknowledged = false;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
            private _userSettings: UserSettingsService,
            private _sanitizer: DomSanitizer,
            private _growler: GrowlerService,
            private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this._userSettings.getGoogleAuthQrCode().pipe(untilDestroyed(this)).subscribe({
            next: (response: GoogleQRResponse) => {
                this.qrCode = this._sanitizer.bypassSecurityTrustHtml(response.qr);
                this.secretKey = response.secret;
                this._cdr.markForCheck();
            },
        });
    }

    onCodeChange(code: string): void {
        this.code = code;
    }

    verify(code: string): void {
        if (code.length !== 6 || this.isSaving || this.isSuccess) {
            return;
        }

        this.isSaving = true;
        this._cdr.markForCheck();

        this._userSettings.finishGoogleAuthSetup(code, this.secretKey).pipe(
            finalize(() => this.isSaving = false),
            untilDestroyed(this),
        ).subscribe({
            next: (response: RecoveryCodesResponse) => {
                this.isSuccess = true;
                this._cdr.markForCheck();
                this._growler.success('Success', 'Google Authenticator has been enabled as a two-factor verification method on your account.');
                this._successTimer = setTimeout(() => this._finish(response?.recoveryCodes ?? []), 800);
            },
            error: () => {
                this._triggerError();
            },
        });
    }

    /**
     * Finish enrollment. When the server issued recovery codes (feature enabled, first method
     * enrolled), hand over to the show-once stage and hold completion until the user acknowledges
     * them; otherwise complete immediately.
     *
     * The codes render INSIDE this step rather than in their own dialog: enrollment is not done
     * until they are saved, and a separate dialog both stacked on the still-open host and let the
     * user into the app first.
     */
    private _finish(recoveryCodes: string[]): void {
        if (recoveryCodes.length) {
            this.recoveryCodes = recoveryCodes;
            this.stage = 'codes';
            this.showingRecoveryCodes.emit();
            this._cdr.markForCheck();
            return;
        }
        this.completed.emit();
    }

    /** Show-once stage acknowledged — enrollment is complete. */
    onCodesAcknowledged(): void {
        if (!this.codesAcknowledged) {
            return;
        }
        this.completed.emit();
    }

    onBack(): void {
        this.cancelled.emit();
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
    }

    private _triggerError(): void {
        this.hasError = true;
        this._cdr.markForCheck();
        this._growler.error('Error', 'We were unable to verify that code. Please try again.');
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._otp?.reset();
            this._cdr.markForCheck();
        }, 600);
    }
}
